import styles from "./ui.module.css";

export function Button({
  children,
  variant = "primary",
  loading = false,
  disabled = false,
  type = "button",
  fullWidth = true,
  onClick,
  ...rest
}) {
  return (
    <button
      type={type}
      className={`${styles.btn} ${styles[variant] || styles.primary} ${fullWidth ? styles.full : ""}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={onClick}
      {...rest}
    >
      {loading ? <span className={styles.spinner} aria-hidden="true" /> : null}
      <span>{children}</span>
    </button>
  );
}

export function Field({
  id,
  label,
  hint,
  error,
  children,
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={styles.field}>
      {label ? <label className={styles.label} htmlFor={id}>{label}</label> : null}
      {children}
      {hint ? <p id={hintId} className={styles.hint}>{hint}</p> : null}
      {error ? <p id={errorId} className={styles.error} role="alert">{error}</p> : null}
    </div>
  );
}

export function Input({ id, error, hint, className = "", ...rest }) {
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") || undefined;
  return (
    <input
      id={id}
      className={`${styles.control} ${error ? styles.invalid : ""} ${className}`}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
      {...rest}
    />
  );
}

export function Select({ id, error, hint, children, className = "", ...rest }) {
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") || undefined;
  return (
    <select
      id={id}
      className={`${styles.control} ${error ? styles.invalid : ""} ${className}`}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
      {...rest}
    >
      {children}
    </select>
  );
}

export function TextArea({ id, error, hint, className = "", ...rest }) {
  const describedBy = [hint ? `${id}-hint` : null, error ? `${id}-error` : null].filter(Boolean).join(" ") || undefined;
  return (
    <textarea
      id={id}
      className={`${styles.control} ${styles.textarea} ${error ? styles.invalid : ""} ${className}`}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
      {...rest}
    />
  );
}

export function Alert({ tone = "error", children }) {
  const toneClass = {
    error: styles.alertError,
    success: styles.alertSuccess,
    info: styles.alertInfo,
  }[tone] || styles.alertError;
  return (
    <div className={`${styles.alert} ${toneClass}`} role="alert">
      <p>{children}</p>
    </div>
  );
}

export function Card({ children, className = "" }) {
  return <div className={`${styles.card} ${className}`}>{children}</div>;
}

export function EmptyState({ title, description }) {
  return (
    <div className={styles.empty}>
      {title ? <h3>{title}</h3> : null}
      {description ? <p>{description}</p> : null}
    </div>
  );
}

export function Badge({ children, tone = "neutral" }) {
  const toneClass = {
    neutral: styles.badgeNeutral,
    success: styles.badgeSuccess,
    warning: styles.badgeWarning,
  }[tone] || styles.badgeNeutral;
  return <span className={`${styles.badge} ${toneClass}`}>{children}</span>;
}

export function Spinner({ label = "Carregando" }) {
  return (
    <div className={styles.center} role="status" aria-live="polite">
      <span className={styles.spinner} aria-hidden="true" />
      <p>{label}</p>
    </div>
  );
}
